import CloseIcon from '@mui/icons-material/Close';
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  TextField,
} from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { FormikProps } from 'formik';
import type React from 'react';
import type { ChangeEvent } from 'react';
import { Waypoint } from 'react-waypoint';
import styles from 'src/components/FieldInput/FieldInputStyles';
import { HTML_FIELD_TYPE } from 'src/constants';
import { parseDateInputValue, serializeDateValue } from 'src/helpers/dateFields';

interface FieldChoice {
  data?: string;
}

interface Field {
  slug: string;
  label?: string;
  fieldType?: string;
  isEditable?: boolean;
  choices?: string[];
  value?: FieldChoice[] | string | string[];
}

interface FormValues {
  [key: string]: string | string[] | undefined | null;
}

interface FieldInputProps {
  field: Field;
  formik: FormikProps<FormValues>;
  viewModel?: string;
}

interface MultiSelectFieldProps {
  disabled: boolean;
  multiselectOnChange: (e: SelectChangeEvent<string[]>, id: string) => void;
  value: string[];
  field: Field;
}

interface CheckboxFieldProps {
  disabled: boolean;
  field: Field;
  formik: FormikProps<FormValues>;
  classes: ReturnType<typeof styles>;
}

/**
 * Resolve the value shown by a PD (Pulldown) Select against its choice list.
 * Exact case match wins. Falls back to a case-insensitive match only when there
 * is exactly one — never silently collapses case-variant choices. Returns the
 * raw value as a string if no resolution applies (out-of-range / orphaned),
 * and `''` for any non-string input so MUI doesn't get an unexpected type.
 *
 * The exact-match-first / single-fuzzy-only contract is load-bearing: a prior
 * implementation lowercased everything on save and corrupted stored case
 * (see FieldInput.test.tsx for the invariants).
 */
export const resolvePulldownSelectValue = (raw: unknown, choices: unknown[]): string => {
  if (typeof raw !== 'string' || raw === '') return '';
  const stringChoices = choices.filter((c): c is string => typeof c === 'string');
  if (stringChoices.includes(raw)) return raw;
  const lowered = raw.toLowerCase();
  const matches = stringChoices.filter((c) => c.toLowerCase() === lowered);
  return matches.length === 1 ? matches[0] : raw;
};

/** Get DatePicker views, format, and initial view based on field precision */
const getDatePickerConfig = (fieldType?: string) => {
  switch (fieldType) {
    case 'YO':
      return { views: ['year'] as const, format: 'yyyy', openTo: 'year' as const };
    case 'YM':
      return { views: ['year', 'month'] as const, format: 'yyyy-MM', openTo: 'month' as const };
    case 'DA':
    case 'DT':
    default:
      return { views: ['year', 'month', 'day'] as const, format: 'yyyy-MM-dd', openTo: 'day' as const };
  }
};

const FieldInput: React.FC<FieldInputProps> = (props) => {
  const classes = styles();

  const { field, formik } = props;
  const disabled = !field?.isEditable;

  const onSelectChange = (e: SelectChangeEvent<string>, id: string): void => {
    const value = e.target.value;
    formik.setFieldValue(id, value);
  };

  const radioButtonOnChange = (e: ChangeEvent<HTMLInputElement>, id: string): void => {
    const value = e.target.value;
    formik.setFieldValue(id, value);
  };

  const multiselectOnChange = (e: SelectChangeEvent<string[]>, id: string): void => {
    if (e.target.value.length === 0) {
      formik.setFieldValue(id, '');
    }
    const value = e.target.value;
    formik.setFieldValue(id, value);
  };

  const datetimeOnChange = (date: Date | null, id: string, fieldType?: string): void => {
    formik.setFieldValue(id, serializeDateValue(date, fieldType));
  };

  const clearDate = (e: React.MouseEvent, id: string): void => {
    e.preventDefault();
    formik.setFieldValue(id, '');
  };

  const onTextInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: string): void => {
    const value = e.target.value;
    formik.setFieldValue(id, value);
  };

  if (field?.fieldType === 'RD') {
    const choices = field?.choices;

    if (formik.values[field?.slug] === undefined || formik.values[field?.slug] === null) {
      return null;
    } else
      return (
        <FormControl disabled={disabled} className={classes.editField} fullWidth>
          <FormLabel className={classes.title}>{field?.label}:</FormLabel>
          <RadioGroup
            value={formik.values[field?.slug] as string}
            onChange={(e) => radioButtonOnChange(e, field?.slug)}
          >
            {choices?.map((choice) => (
              <FormControlLabel key={choice} control={<Radio />} label={choice} value={choice} />
            ))}
          </RadioGroup>
        </FormControl>
      );
  } else if (field?.fieldType === 'CB') {
    return <CheckboxField disabled={disabled} field={field} formik={formik} classes={classes} />;
  } else if (field?.fieldType === 'PD') {
    const choices = field?.choices || [];
    const currentVal = resolvePulldownSelectValue(formik.values[field?.slug], choices);

    return (
      <FormControl disabled={disabled} className={classes.editField} fullWidth>
        <FormLabel className={classes.title}>{field?.label}:</FormLabel>
        <Select<string> value={currentVal} disabled={disabled} onChange={(e) => onSelectChange(e, field?.slug)}>
          {choices.map((choice) => (
            <MenuItem key={choice} value={choice}>
              {choice}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  } else if ([HTML_FIELD_TYPE, 'TA'].includes(field?.fieldType || '')) {
    return (
      <FormControl disabled={disabled} className={classes.editField} fullWidth>
        <FormLabel className={classes.title}>{field?.label}:</FormLabel>
        <TextField
          className="cyp-edit-text-field"
          onChange={(e) => onTextInputChange(e, field?.slug)}
          value={formik.values[field?.slug] || ''}
          multiline
          disabled={disabled}
          maxRows={10}
        />
      </FormControl>
    );
  } else if (
    field?.fieldType === 'DT' ||
    field?.fieldType === 'DA' ||
    field?.fieldType === 'YM' ||
    field?.fieldType === 'YO'
  ) {
    const { views, format, openTo } = getDatePickerConfig(field.fieldType);
    const rawValue = formik.values[field?.slug];
    const parsedValue = typeof rawValue === 'string' ? parseDateInputValue(rawValue, field.fieldType) : null;
    return (
      <FormControl disabled={disabled} className={classes.editField} fullWidth>
        <FormLabel className={classes.title}>{field?.label}:</FormLabel>
        <div style={{ display: 'flex', width: '100%' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disabled={disabled}
              views={[...views]}
              openTo={openTo}
              format={format}
              value={parsedValue}
              onChange={(newValue) => datetimeOnChange(newValue, field.slug, field.fieldType)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
          {formik.values[field?.slug] && (
            <CloseIcon className={classes.clearButton} onClick={(e) => clearDate(e, field.slug)} />
          )}
        </div>
      </FormControl>
    );
  } else if (field?.fieldType === 'MS') {
    const value: string[] =
      formik.values[field.slug] === undefined || formik.values[field.slug] === null || formik.values[field.slug] === ''
        ? []
        : (formik.values[field.slug] as string[]);
    return (
      <MultiSelectField disabled={disabled} field={field} value={value} multiselectOnChange={multiselectOnChange} />
    );
  }

  const value = formik.values[field?.slug] as string;
  return (
    <FormControl disabled={disabled} className={classes.editField} fullWidth>
      <FormLabel className={classes.title}>{field?.label}:</FormLabel>
      <TextField
        className="cyp-edit-text-field"
        onChange={(e) => onTextInputChange(e, field?.slug)}
        value={value || ''}
        disabled={disabled}
        type="text"
      />
    </FormControl>
  );
};

const CheckboxField: React.FC<CheckboxFieldProps> = ({ disabled, field, formik, classes }: CheckboxFieldProps) => {
  const checkboxOptions = field?.choices || [];
  const checkedValues = (formik.values[field.slug] as string[] | undefined) ?? [];

  const handleCheckboxChange = (choice: string): void => {
    const next = checkedValues.includes(choice)
      ? checkedValues.filter((v) => v !== choice)
      : [...checkedValues, choice];
    formik.setFieldValue(field.slug, next);
  };

  return (
    <FormControl disabled={disabled} className={classes.editField} fullWidth>
      <FormLabel className={classes.title}>{field?.label}:</FormLabel>
      {checkboxOptions.map((choice) => (
        <div key={choice}>
          <Checkbox
            id={`checkbox-${choice}`}
            checked={checkedValues.includes(choice)}
            onChange={() => handleCheckboxChange(choice)}
          />
          <label htmlFor={`checkbox-${choice}`}>{choice}</label>
        </div>
      ))}
    </FormControl>
  );
};

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({ disabled, multiselectOnChange, value, field }) => {
  const classes = styles();

  const handleScrollThreshold = (): void => {
    // Placeholder for scroll threshold handling
  };

  return (
    <FormControl disabled={disabled} className={classes.editField} fullWidth>
      <FormLabel className={classes.title}>{field?.label}:</FormLabel>
      <Select<string[]>
        multiple
        disabled={disabled}
        value={value}
        onChange={(e) => multiselectOnChange(e, field.slug)}
        renderValue={(selected) => (
          <div className={classes.chips}>
            {(selected as string[]).map((val) => val && <Chip key={val} label={val} className={classes.chip} />)}
          </div>
        )}
      >
        {field.choices
          ? field?.choices.map((choice, i) => (
              <MenuItem key={choice} value={choice}>
                <Checkbox checked={value.includes(choice)} />
                <ListItemText primary={choice} />
                {i === (field?.choices?.length || 0) - 40 && (
                  <Waypoint fireOnRapidScroll={true} onEnter={handleScrollThreshold} />
                )}
              </MenuItem>
            ))
          : ''}
      </Select>
    </FormControl>
  );
};

export default FieldInput;
